import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import UserAvatar from "@/components/UserAvatar";
import { fmtDate, fmtRelative } from "@/lib/format";
import { RefreshCw, Search, Download, ScrollText, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ACTION_LABEL = {
  "client.create": "Client created",
  "client.update": "Client updated",
  "client.archive": "Client archived",
  "client.delete_hard": "Client permanently deleted",
  "service.add": "Service added",
  "service_catalog.create": "Service catalog: added",
  "service_catalog.update": "Service catalog: updated",
  "service_catalog.delete": "Service catalog: deleted",
  "project.create": "Project created",
  "project.update": "Project updated",
  "task.create": "Task created",
  "task.update": "Task updated",
  "task.status": "Task status changed",
  "task.transfer": "Task transferred",
  "deliverable.create": "Deliverable created",
  "quotation.create": "Quotation created",
  "quotation.convert": "Quotation converted",
  "payment.create": "Payment recorded",
  "payment.void": "Payment voided",
  "user.create": "Team member created",
  "user.update": "Team member updated",
  "user.approve": "Access request approved",
  "user.reject": "Access request rejected",
  "user.disable": "Team member disabled",
  "user.enable": "Team member re-enabled",
  "user.reset_password": "Password reset (by admin)",
  "user.change_password_self": "Password changed (self)",
  "seed.complete": "Demo data seeded",
};

const ENTITY_COLOR = {
  client: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  task: "bg-primary/15 text-primary",
  project: "bg-purple-500/15 text-purple-700 dark:text-purple-300",
  deliverable: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  quotation: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
  payment: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  user: "bg-pink-500/15 text-pink-700 dark:text-pink-300",
  service_catalog: "bg-orange-500/15 text-orange-700 dark:text-orange-300",
  client_service: "bg-cyan-500/15 text-cyan-700 dark:text-cyan-300",
  system: "bg-muted text-muted-foreground",
};

const ENTITY_DEST = {
  client: (id) => `/clients/${id}`,
  task: () => `/tasks`,
  project: () => `/projects`,
  deliverable: () => `/deliverables`,
  quotation: () => `/quotations`,
  payment: () => `/finance`,
  user: () => `/team`,
  service_catalog: () => `/settings`,
};

export default function AuditLog() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [facets, setFacets] = useState({ actions: [], entity_types: [] });
  const [q, setQ] = useState("");
  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (q) params.q = q;
      if (actorId) params.actor_id = actorId;
      if (action) params.action = action;
      if (entityType) params.entity_type = entityType;
      if (start) params.start = start;
      if (end) params.end = end;
      const r = await api.get("/activity", { params });
      setRows(r.data);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/audit-log/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [q, actorId, action, entityType, start, end, limit]);

  const summary = useMemo(() => {
    const byActor = {}; const byEntity = {};
    rows.forEach((r) => {
      byActor[r.actor_name] = (byActor[r.actor_name] || 0) + 1;
      byEntity[r.entity_type] = (byEntity[r.entity_type] || 0) + 1;
    });
    return { byActor, byEntity };
  }, [rows]);

  if (user && user !== false && user !== null && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const clearFilters = () => { setQ(""); setActorId(""); setAction(""); setEntityType(""); setStart(""); setEnd(""); };
  const hasFilters = q || actorId || action || entityType || start || end;

  const exportCsv = () => {
    const header = ["When", "Actor", "Action", "Entity Type", "Entity Label", "Entity ID", "Meta"].join(",");
    const lines = rows.map((r) => [
      r.created_at, r.actor_name, r.action, r.entity_type,
      `"${(r.entity_label || "").replace(/"/g, '""')}"`,
      r.entity_id, `"${JSON.stringify(r.meta || {}).replace(/"/g, '""')}"`
    ].join(","));
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `digitalis-audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Compliance</div>
          <h1 className="text-4xl font-black tracking-tight flex items-center gap-3"><ScrollText size={30} strokeWidth={1.75} />Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">Every change made across the CRM. Super admin only.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={load} title="Refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
          <Button data-testid="audit-export-csv" variant="outline" onClick={exportCsv}><Download size={14} className="mr-1" />Export CSV</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center border border-border rounded-md p-3 bg-card">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actor, action or label…" className="pl-9 w-64" />
        </div>
        <Select value={actorId || "__all__"} onValueChange={(v) => setActorId(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="audit-actor" className="w-48"><SelectValue placeholder="Any actor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any actor</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={action || "__all__"} onValueChange={(v) => setAction(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="audit-action" className="w-56"><SelectValue placeholder="Any action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any action</SelectItem>
            {facets.actions.map((a) => <SelectItem key={a} value={a}>{ACTION_LABEL[a] || a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityType || "__all__"} onValueChange={(v) => setEntityType(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="audit-entity" className="w-44"><SelectValue placeholder="Any type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any type</SelectItem>
            {facets.entity_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">From</span><Input type="date" className="w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="audit-start" /></div>
        <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">To</span><Input type="date" className="w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="audit-end" /></div>
        <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v))}>
          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="100">100</SelectItem>
            <SelectItem value="200">200</SelectItem>
            <SelectItem value="500">500</SelectItem>
            <SelectItem value="1000">1000</SelectItem>
          </SelectContent>
        </Select>
        {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>}
        <div className="ml-auto text-xs font-mono text-muted-foreground">{rows.length} events</div>
      </div>

      {/* Table */}
      <div className="border border-border rounded-md overflow-hidden bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="px-3 py-2.5">When</th>
              <th className="px-3 py-2.5">Actor</th>
              <th className="px-3 py-2.5">Action</th>
              <th className="px-3 py-2.5">Entity</th>
              <th className="px-3 py-2.5">Label / target</th>
              <th className="px-3 py-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No activity for this filter.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="data-row border-t border-border cursor-pointer" onClick={() => setDetail(r)} data-testid={`audit-row-${r.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs">
                  <div>{fmtRelative(r.created_at)}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtDate(r.created_at)}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <UserAvatar name={r.actor_name} size={22} />
                    <span className="text-sm">{r.actor_name || "System"}</span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span className="font-mono text-xs">{ACTION_LABEL[r.action] || r.action}</span>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center h-5 px-2 rounded font-mono text-[10px] font-semibold uppercase tracking-widest ${ENTITY_COLOR[r.entity_type] || "bg-muted"}`}>
                    {r.entity_type || "system"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-sm">{r.entity_label || "—"}</td>
                <td className="px-3 py-2.5 text-muted-foreground"><ArrowRight size={14} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Audit event</div>
            <DialogTitle>{ACTION_LABEL[detail?.action] || detail?.action}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <Row label="When">{fmtDate(detail.created_at)} — {fmtRelative(detail.created_at)}</Row>
              <Row label="Actor"><span className="font-semibold">{detail.actor_name || "System"}</span></Row>
              <Row label="Action"><span className="font-mono text-xs">{detail.action}</span></Row>
              <Row label="Entity type"><span className="font-mono text-xs">{detail.entity_type || "—"}</span></Row>
              <Row label="Entity label">{detail.entity_label || "—"}</Row>
              <Row label="Entity ID"><span className="font-mono text-[10px] break-all">{detail.entity_id}</span></Row>
              {detail.meta && Object.keys(detail.meta).length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Details</div>
                  <pre className="bg-muted rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap">{JSON.stringify(detail.meta, null, 2)}</pre>
                </div>
              )}
              {ENTITY_DEST[detail.entity_type] && (
                <Button data-testid="audit-open-entity" onClick={() => { const to = ENTITY_DEST[detail.entity_type](detail.entity_id); setDetail(null); nav(to); }} className="w-full bg-primary hover:bg-primary/90">
                  Open {detail.entity_type} <ArrowRight size={14} className="ml-1" />
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between border-b border-border pb-2 gap-4">
      <span className="font-mono text-[10px] uppercase text-muted-foreground shrink-0">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}
